/**
 * Переключатель масштаба рабочей области: «− 100 % +».
 *
 * Выделен из «Заказов», где он появился первым: та же тройка кнопок нужна во
 * врачах, клиниках, конструкциях, документах, сотрудниках и финансах. Копия
 * разметки в каждом разделе означала бы, что правка вида — это шесть правок,
 * и один раздел рано или поздно отстанет от остальных.
 *
 * Сам переключатель НЕ масштабируется вместе с содержимым: он стоит снаружи
 * блока с `zoom`, рядом с прочими кнопками раздела. Иначе на 40 % до него
 * было бы не попасть пальцем, а на 140 % он распирал бы шапку.
 */

import { Minus, Plus } from 'lucide-react';

import type { useTableZoom } from './useTableZoom.js';
import { ZOOM_STEP } from './useTableZoom.js';

export function ZoomControl({
  zoom,
  className = '',
  /** Подсказка к «мельче»: в каждом разделе на экране помещается своё. */
  hint = 'Мельче — на экран влезет больше',
}: {
  zoom: ReturnType<typeof useTableZoom>;
  className?: string;
  hint?: string;
}) {
  // На узком экране разделы, кроме журнала заказов, переключателя не имеют.
  if (!zoom.доступен) return null;

  return (
    <div
      className={`flex flex-shrink-0 items-center gap-0.5 rounded-lg border ${className}`}
      style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
    >
      <button
        onClick={() => zoom.zoomBy(-ZOOM_STEP)}
        disabled={!zoom.canZoomOut}
        className="rounded-l-lg px-2 py-1.5 hover:bg-black/5 disabled:opacity-30"
        title={hint}
        aria-label="Мельче"
      >
        <Minus size={13} className="text-ink2" />
      </button>
      {/*
        Само число — кнопка возврата к 100 %. Отдельная кнопка «сброс» заняла
        бы место втрое, а нажать на цифру, чтобы вернуть обычный размер, —
        первое, что пробуют.
      */}
      <button
        onClick={zoom.reset}
        className="tabular w-11 py-1.5 text-center text-xs text-ink2 hover:bg-black/5"
        title="Вернуть обычный размер"
      >
        {zoom.zoom}%
      </button>
      <button
        onClick={() => zoom.zoomBy(ZOOM_STEP)}
        disabled={!zoom.canZoomIn}
        className="rounded-r-lg px-2 py-1.5 hover:bg-black/5 disabled:opacity-30"
        title="Крупнее"
        aria-label="Крупнее"
      >
        <Plus size={13} className="text-ink2" />
      </button>
    </div>
  );
}
